import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setKind } from 'redux-first-router'
import {
  selectCurrentPage,
  selectQueryParams,
  selectCurrentPayload,
} from 'selectors/location'

const buildTabPageAction = ({ tabId, kind }) => (dispatch, getState) => {
  const state = getState()
  const pageType = selectCurrentPage(state)
  const pagePayload = selectCurrentPayload(state)
  const currentPageQuery = selectQueryParams(state)

  const pageAction = {
    type: pageType,
    payload: {
      ...pagePayload,
      tab: tabId,
    },
    meta: {
      query: currentPageQuery,
    },
  }

  return dispatch(kind ? setKind(pageAction, kind) : pageAction)
}

export const useTabs = ({ tabs, defaultTabId }) => {
  const dispatch = useDispatch()
  const pagePayload = useSelector(selectCurrentPayload)
  const { tab: tabId = defaultTabId } = pagePayload
  const defaultActiveTab = useMemo(
    () => (tabs || []).find(t => t.key === defaultTabId),
    [defaultTabId, tabs]
  )
  const activeTab = useMemo(
    () => (tabs || []).find(t => t.key === tabId) || defaultActiveTab,
    [tabId, tabs, defaultActiveTab]
  )

  const onTabChange = useCallback(
    inputTabId => {
      if (inputTabId === tabId) return
      const payload = {}
      if (inputTabId) {
        payload.tab = inputTabId
      }
      dispatch(buildTabPageAction({ tabId: inputTabId }))
    },
    [dispatch, tabId]
  )

  return {
    onTabChange,
    tabs,
    activeTab,
    ActiveTabComponent: activeTab?.component,
    activeTabId: tabId,
    pagePayload,
  }
}

export const useLink = (inputAction, { preserveQuery = false } = {}) => {
  const currentPageQuery = useSelector(selectQueryParams)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const action = useMemo(() => inputAction, [])

  const pageAction = useMemo(
    () => {
      if (preserveQuery) {
        return {
          ...action,
          meta: { query: currentPageQuery },
        }
      }
      return action
    },
    [action, currentPageQuery, preserveQuery]
  )

  return pageAction
}
